import moment from 'moment';

const evaluateFieldConditions = (conditions, values, context = {}) => {
  if (!conditions) return true;

  const {
    parentCode,
    arrayIndex = 0,
    fieldPathIndex,
    subcheckIndex,
    company,
    enabledCompanyChecks,
    companyCheckConfigurations,
    companyCheckConfig,
    candidate,
    candidateValues,
    checkDetails,
    checkExtraData,
    checkType,
    subCheckDetails,
    subCheckExtraDetails,
    selectedOptionsContext,
  } = context;

  // Helper for getting nested values from an object using dot notation
  // Optionally handles automatic subcheck index resolution for sequential arrays
  // (when subcheckIndex is provided and value is a sequential array)
  const getNestedValue = (obj, path, useSubcheckResolution = false) => {
    if (!path || !obj) return undefined;
    const keys = path.split('.');
    let value = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
        // If subcheck resolution is enabled, current value is a sequential array, and we have a subcheckIndex,
        // automatically resolve to that item (for subcheck array resolution in candidate_values.* paths)
        if (
          useSubcheckResolution &&
          subcheckIndex !== undefined &&
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((_, i) => i in value)
        ) {
          value = value[subcheckIndex];
          if (value === undefined) return undefined;
        }
      } else {
        return undefined;
      }
    }

    return value;
  };

  const evaluateOperation = (conditionValue, operation, targetValue) => {
    // Special cases for existence checks
    if (operation === 'exists') {
      return conditionValue !== undefined && conditionValue !== null && conditionValue !== '';
    }
    if (operation === 'not_exists') {
      // Treat undefined, null, and empty strings as "not existing"
      // Empty strings often come from unchecked checkboxes or empty form fields
      return conditionValue === undefined || conditionValue === null || conditionValue === '';
    }

    // For all other operations, return false if value doesn't exist
    if (conditionValue === undefined || conditionValue === null) {
      return false;
    }

    if (operation.startsWith('date_')) {
      // Extract base operation and optional unit suffix
      const unitMatch = operation.match(/_(months|years)$/);
      const unit = unitMatch ? unitMatch[1] : null; // 'months' | 'years' | null
      const baseOp = unit ? operation.replace(/_(months|years)$/, '') : operation;

      // Parse the field value strictly as DD/MM/YYYY
      const fieldDate = moment(conditionValue, 'DD/MM/YYYY', true);
      if (!fieldDate.isValid()) return false;

      const relUnit = unit || 'months'; // default to months when no suffix provided

      const offset =
        typeof targetValue === 'number'
          ? targetValue
          : typeof targetValue === 'string' && targetValue.trim() !== '' && !isNaN(targetValue)
            ? Number(targetValue)
            : null;

      // Build the comparison date:
      // - relative: now + offset in relUnit
      // - absolute: strict DD/MM/YYYY, else strict ISO as fallback
      const compareDate =
        offset !== null
          ? moment().add(offset, relUnit)
          : moment(targetValue, 'DD/MM/YYYY', true).isValid()
            ? moment(targetValue, 'DD/MM/YYYY', true)
            : moment(targetValue, moment.ISO_8601, true);

      if (!compareDate.isValid()) return false;

      switch (baseOp) {
        case 'date_before':
          return fieldDate.isBefore(compareDate);
        case 'date_after':
          return fieldDate.isAfter(compareDate);
        case 'date_same_or_before':
          return fieldDate.isSameOrBefore(compareDate);
        case 'date_same_or_after':
          return fieldDate.isSameOrAfter(compareDate);
        default:
          return false; // unknown date op
      }
    }

    // Convert boolean strings to actual booleans for comparison
    const normalizeValue = value => {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
        if (value.toLowerCase() === 'yes') return 'yes';
        if (value.toLowerCase() === 'no') return 'no';
        if (!isNaN(value)) return parseFloat(value);
      }
      return value;
    };

    const normalizedConditionValue = normalizeValue(conditionValue);
    const normalizedTargetValue = normalizeValue(targetValue);

    switch (operation) {
      case 'is':
        return normalizedConditionValue === normalizedTargetValue;
      case 'is_not':
        return normalizedConditionValue !== normalizedTargetValue;
      case 'in':
        return targetValue ? targetValue.includes(conditionValue) : true;
      case 'not_in':
        return targetValue ? !targetValue.includes(conditionValue) : true;
      case 'contains':
        return conditionValue ? conditionValue.includes(targetValue) : true;
      case 'not_contains':
        return conditionValue ? !conditionValue.includes(targetValue) : true;
      case 'not_empty':
        if (Array.isArray(conditionValue)) {
          return conditionValue?.length > 0;
        }
        return true;
      case 'is_empty':
        if (Array.isArray(conditionValue)) {
          return conditionValue?.length === 0;
        }
        return true;
      case 'less_than':
        return (
          typeof normalizedConditionValue === 'number' &&
          typeof normalizedTargetValue === 'number' &&
          normalizedConditionValue < normalizedTargetValue
        );
      case 'greater_than':
        return (
          typeof normalizedConditionValue === 'number' &&
          typeof normalizedTargetValue === 'number' &&
          normalizedConditionValue > normalizedTargetValue
        );
      default:
        return true;
    }
  };

  // Map of code prefixes to their context objects
  // Keys match TemplateUtil BACKEND_RESOLVE_PREFIXES / FieldResolverService
  const contextSourceMap = {
    'company.': company,
    'enabled_company_checks.': enabledCompanyChecks ?? {},
    'company_check_configurations.': companyCheckConfigurations ?? {},
    'configuration.': companyCheckConfig,
    'candidate.': candidate,
    'candidate_values.': candidateValues,
    'check_type.': checkType ?? {},
    'check_details.': checkDetails,
    'check_extra_data.': checkExtraData ?? {},
    'subcheck_details.': subCheckDetails,
    'subcheck_extra_details.': subCheckExtraDetails ?? {},
  };

  // Check if code has a known source prefix (configuration.*, candidate.*, etc.)
  // Form field codes don't need prefixes - they're the default
  const hasKnownPrefix = code => {
    if (!code || typeof code !== 'string') return false;

    // Check if code has a known backend source prefix
    for (const prefix of Object.keys(contextSourceMap)) {
      if (code.startsWith(prefix)) {
        return true;
      }
    }

    return false;
  };

  // Helper to get value from context source map
  const getContextValue = code => {
    // Find matching prefix in map
    for (const [prefix, contextObj] of Object.entries(contextSourceMap)) {
      if (code.startsWith(prefix)) {
        if (!contextObj) return undefined;
        const path = code.substring(prefix.length);
        // Enable subcheck array resolution for candidate_values.* paths
        const useSubcheckResolution = prefix === 'candidate_values.';
        return getNestedValue(contextObj, path, useSubcheckResolution);
      }
    }
    return undefined;
  };

  const evaluateCondition = condition => {
    if (!condition) return true;

    // If this is a nested AND/OR condition
    if (condition?.op === 'and' || condition?.op === 'or') {
      return evaluateLogicalGroup(condition);
    }

    // Regular condition evaluation
    let { op, code, value, property } = condition;
    if (!op || !code) return true;

    // Ensure code is a string for string operations
    if (typeof code !== 'string') return true;

    // Normalize *_years operations into *_months
    if (op.endsWith('_years')) {
      op = op.replace('_years', '_months');
      value = parseFloat(value) * 12;
    }

    // Special case for array index conditions
    if (code === 'array_index') {
      return evaluateOperation(arrayIndex, op, value);
    }

    // Fallback 0 here; PHP uses null for non-array (see conditions_util).
    if (property === 'length') {
      const raw = hasKnownPrefix(code) ? getContextValue(code) : values[code];
      const len = Array.isArray(raw) ? raw.length : 0;
      return evaluateOperation(len, op, value);
    }

    // Handle context source paths (configuration.*, details.*, candidate_values.*, etc.)
    // Only codes with known prefixes are treated as source references
    // Form field codes (no prefix) are the default and looked up directly in values
    if (hasKnownPrefix(code)) {
      const conditionValue = getContextValue(code);
      // If context value is undefined (context object is null), condition fails
      // This is expected behavior - if configuration doesn't exist, condition can't pass
      return evaluateOperation(conditionValue, op, value);
    }

    if (property) {
      // Evaluate the value of all items in an array matching the target field code
      // Must provide parentCode.childCode in the code field
      if (property === 'array_values') {
        const [parentCode, childCode] = code.split('.', 2);
        const targetArray = values[parentCode];
        if (!Array.isArray(targetArray)) return false;

        for (const item of targetArray) {
          if (!item || typeof item !== 'object') continue;
          if (!evaluateOperation(item[childCode], op, value)) {
            return false;
          }
        }
        return true;
      }

      // Find the field definition in fieldPathIndex
      const fieldDefinition = fieldPathIndex?.[code];

      if (fieldDefinition) {
        // Check if property exists in fieldPathIndex
        if (
          property === 'type' ||
          property === 'sectionCode' ||
          property === 'subsectionCode' ||
          property === 'groupCode'
        ) {
          const fieldPropertyValue = fieldDefinition[property];
          return evaluateOperation(fieldPropertyValue, op, value);
        }

        // Check conditionsFieldProperties for custom properties
        if (
          fieldDefinition.conditionsFieldProperties &&
          fieldDefinition.conditionsFieldProperties[property] !== undefined
        ) {
          const fieldPropertyValue = fieldDefinition.conditionsFieldProperties[property];
          return evaluateOperation(fieldPropertyValue, op, value);
        }
      }
      return false;
    }

    if (code.includes('.')) {
      const [baseFieldCode, ...propertyPath] = code.split('.');
      if (selectedOptionsContext && selectedOptionsContext[baseFieldCode]) {
        const selectedOption = selectedOptionsContext[baseFieldCode];
        const propertyPathStr = propertyPath.join('.');
        const conditionValue = getNestedValue(selectedOption, propertyPathStr);
        return evaluateOperation(conditionValue, op, value);
      }
    }

    // If we pass in a parentField and arrayIndex we check whether the field is in the same array first and then try values
    let fieldValue;
    if (parentCode && Array.isArray(values[parentCode])) {
      // For array items, check nested value within the array item first
      fieldValue =
        values[parentCode][arrayIndex]?.[code] ??
        getNestedValue(values[parentCode][arrayIndex], code);
      // Fallback to top-level if not found in array item
      if (fieldValue === undefined) {
        fieldValue = values[code] ?? getNestedValue(values, code);
      }
    } else {
      // Try direct access first, then try dot notation for nested properties
      fieldValue = values[code] ?? getNestedValue(values, code);
    }

    const result = evaluateOperation(fieldValue, op, value);

    return result;
  };

  const evaluateLogicalGroup = group => {
    if (!group.conditions?.length) return true;

    const results = group.conditions.map(condition => evaluateCondition(condition));
    return group.op === 'and' ? results.every(Boolean) : results.some(Boolean);
  };

  // Handle array of conditions (treat as AND)
  if (Array.isArray(conditions)) {
    return conditions.every(condition => evaluateCondition(condition));
  }

  // Handle single condition or logical group
  return evaluateCondition(conditions);
};

export default evaluateFieldConditions;
